/**
 * The credential scrub every Sukha Sentry client runs on events and spans.
 *
 * Moved here from `apps/api` (TX-5459 review) so the web server, edge and
 * browser clients run the same one: Next's root span records `http.target`
 * from the request URL WITH its query, and @sentry/nextjs 11 does not filter
 * that through `dataCollection.urlQueryParams` — so a sampled /accept-token
 * trace carried a working Clerk sign-in token.
 */

/** Query parameters that carry a credential in Sukha URLs (and common ones). */
export const SENTRY_SECRET_QUERY_PARAMS = [
  "token",
  "ticket",
  "code",
  "state",
  "secret",
  "key",
  "signature",
  "sig",
  "password",
  "access_token",
  "refresh_token",
  // The desktop's device-link poll secret, sent as a query parameter by
  // builds older than its header (TX-5468).
  "pollSecret",
] as const;
const SECRET_NAME = `(?:${SENTRY_SECRET_QUERY_PARAMS.join("|")})`;
// `#` too: a credential can be the fragment's first parameter (`#token=`,
// TX-5468), which a browser keeps out of requests but not out of breadcrumbs.
const SECRET_IN_QUERY = new RegExp(`((?:^|[?&#])${SECRET_NAME}=)[^&#\\s"]*`, "gi");
/**
 * The same parameter inside a percent-encoded return path, once or twice
 * encoded (`%3F`/`%26` or `%253F`/`%2526`, then `%3D` or `%253D`). A signed-out
 * member on /connect-desktop?code= is sent to
 * /landing?deviceLinkReturn=%2Fconnect-desktop%3Fcode%3D… (TX-5459). The value
 * runs to the next encoded or plain `&`.
 */
const SECRET_IN_ENCODED_QUERY = new RegExp(
  `(%(?:25)?(?:3F|26)${SECRET_NAME}%(?:25)?3D)(?:(?!%(?:25)?26)[^&#\\s"])*`,
  "gi"
);

/** True when `text` carries a credential query parameter, plain or percent-encoded. */
export function hasQuerySecret(text: string): boolean {
  return [SECRET_IN_QUERY, SECRET_IN_ENCODED_QUERY].some((pattern) => {
    pattern.lastIndex = 0;
    return pattern.test(text);
  });
}
/**
 * Field names masked whole. Narrower than SENTRY_SECRET_QUERY_PARAMS on
 * purpose: `code`, `key` and `state` as field names are a Postgres error code, a cache key or an
 * app state — the data an error report exists to carry — and only a secret in
 * a URL (where `state` is the OAuth round-trip value).
 */
/** Deeper than any real event; a bound so the walk can never exhaust the stack. */
const MAX_SCRUB_DEPTH = 32;

// Lower-cased: field names are compared lower-cased (`pollSecret`).
const SECRET_KEYS = new Set<string>(
  SENTRY_SECRET_QUERY_PARAMS.filter((param) => param !== "code" && param !== "key" && param !== "sig" && param !== "state").map(
    (param) => param.toLowerCase()
  )
);

/**
 * Masks credential query parameters anywhere in an event: request URL and
 * query string, span data, breadcrumbs, contexts. The chart routes are guarded
 * by a signed `?token=` that does not expire, and Sentry records URLs on errors
 * and on every transaction span (security review 2026-09-26, L7). A field named
 * like one of the params (a parsed query object) is masked whole.
 *
 * Runs on any value — events, spans, breadcrumbs — and never throws.
 */
export function scrubQuerySecrets<T>(value: T): T {
  // A real event is not plain JSON: sdkProcessingMetadata holds live request
  // objects with circular references, and the first version of this walker
  // followed them until the stack overflowed on every request (preview,
  // THE-SUKHA-COMPANY-SS). So: plain objects and arrays only, each visited
  // once, to a bounded depth, never sdkProcessingMetadata (Sentry does not
  // send it) — and a scrub that fails returns the event unchanged rather than
  // throwing inside Sentry's hook.
  const seen = new WeakSet<object>();
  const walk = (node: unknown, key: string | undefined, depth: number): unknown => {
    if (typeof node === "string") {
      return key && SECRET_KEYS.has(key.toLowerCase())
        ? "[Filtered]"
        : node.replace(SECRET_IN_QUERY, "$1[Filtered]").replace(SECRET_IN_ENCODED_QUERY, "$1[Filtered]");
    }
    if (!node || typeof node !== "object" || depth > MAX_SCRUB_DEPTH || seen.has(node)) return node;
    if (Array.isArray(node)) {
      seen.add(node);
      return node.map((item) => walk(item, undefined, depth + 1));
    }
    const proto = Object.getPrototypeOf(node);
    if (proto !== Object.prototype && proto !== null) return node;
    seen.add(node);
    return Object.fromEntries(
      Object.entries(node).map(([k, v]) => [
        k,
        k === "sdkProcessingMetadata" ? v : walk(v, k, depth + 1),
      ])
    );
  };
  try {
    return walk(value, undefined, 0) as T;
  } catch {
    // See the docblock: a value the walk cannot handle goes out as it came.
    return value;
  }
}
